import React, { useState } from 'react';
import { Box } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ComponentDefinition, COMPONENT_DEFINITIONS } from '@/pages/reports/schemas';
import type { ComponentType } from '@/types/reports';

import heroPreview from '@/assets/images/report/preview/hero.png';
import trendingTopicsPreview from '@/assets/images/report/preview/trending-topics.png';
import assuntosFormatosPreview from '@/assets/images/report/preview/assuntos-formatos.png';
import pontosDeAtencaoPreview from '@/assets/images/report/preview/pontos-de-atencao.png';
import businessPreview from '@/assets/images/report/preview/business.png';
import rodapePreview from '@/assets/images/report/preview/rodape.png';
import tiktokTrendsPreview from '@/assets/images/report/preview/tiktok-trends.png';
import sessaoTematicaPreview from '@/assets/images/report/preview/sessao-tematica.png';
import curadoriaPreview from '@/assets/images/report/preview/curadoria.png';

const COMPONENT_PREVIEW_IMAGES: Partial<Record<ComponentType, string>> = {
  hero: heroPreview,
  'today-carousel': trendingTopicsPreview,
  'news-card': assuntosFormatosPreview,
  'highlights-card': pontosDeAtencaoPreview,
  'business-card': businessPreview,
  'thematic-session': sessaoTematicaPreview,
  footer: rodapePreview,
  'tiktok-trends': tiktokTrendsPreview,
  curatorship: curadoriaPreview,
};

const ADDABLE_COMPONENT_TYPES: ComponentType[] = [
  'hero',
  'today-carousel',
  'tiktok-trends',
  'highlights-card',
  'news-card',
  'business-card',
  'thematic-session',
  'curatorship',
];

interface AvailableComponentsCardProps {
  onAddComponent: (_componentDef: ComponentDefinition) => void;
  hideHeader?: boolean;
  disabled?: boolean;
  disabledTypes?: ComponentType[];
}

export const AvailableComponentsCard: React.FC<AvailableComponentsCardProps> = ({
  onAddComponent,
  hideHeader = false,
  disabled = false,
  disabledTypes = [],
}) => {
  const [hoveredType, setHoveredType] = useState<ComponentType | null>(null);

  const getComponentIcon = () => {
    return <Box className="w-5 h-5 text-gray-500" />;
  };

  const grid = (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {ADDABLE_COMPONENT_TYPES
        .map((type) => COMPONENT_DEFINITIONS.find((c) => c.type === type))
        .filter((c): c is ComponentDefinition => Boolean(c))
        .map((component) => {
        const isTypeDisabled = disabled || disabledTypes.includes(component.type);
        const previewSrcForCard =
          !isTypeDisabled && component.type === hoveredType
            ? COMPONENT_PREVIEW_IMAGES[component.type]
            : null;
        return (
          <div
            key={component.type}
            className="relative"
            onMouseEnter={() => !isTypeDisabled && setHoveredType(component.type)}
            onMouseLeave={() => setHoveredType(null)}
          >
            {previewSrcForCard && (
              <div
                className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-10 w-48 rounded-lg border border-gray-200 bg-white shadow-lg overflow-hidden pointer-events-none"
                style={{ maxHeight: 'min(320px, 60vh)' }}
              >
                <img
                  src={previewSrcForCard}
                  alt="Preview do componente"
                  className="w-full h-auto object-contain object-top block"
                />
              </div>
            )}
            <Button
              variant="outline"
              onClick={() => onAddComponent(component)}
              disabled={isTypeDisabled}
              className="justify-start h-auto p-4 hover:border-gray-400 hover:bg-gray-50 transition-all w-full disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex items-center gap-4 w-full">
                <div className="flex-shrink-0">
                  {getComponentIcon()}
                </div>
                <div className="text-left flex-1 min-w-0">
                  <div className="font-semibold text-gray-900 truncate">{component.name}</div>
                </div>
              </div>
            </Button>
          </div>
        );
      })}
    </div>
  );

  if (hideHeader) {
    return grid;
  }

  return (
    <Card className="relative">
      <CardHeader className="border-b">
        <CardTitle className="text-xl">Componentes</CardTitle>
      </CardHeader>
      <CardContent className="pt-6">
        {grid}
      </CardContent>
    </Card>
  );
};
